import React, { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";

export const PLACEHOLDERS = ["{naam}", "{camping}", "{aankomst}", "{vertrek}", "{plaats}", "{medewerker}"];
const blank = () => ({ name: "", lang: "nl", subject: "", body: "" });

export default function TemplateManager({ onClose }) {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/mailbox/templates").then(({ data }) => setRows(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (edit.id) await api.put(`/mailbox/templates/${edit.id}`, edit); else await api.post("/mailbox/templates", edit);
      setEdit(null); load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`${t("mb_tpl_delete_confirm")}\n\n${r.name}`)) return;
    try { await api.delete(`/mailbox/templates/${r.id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl bg-background" data-testid="template-manager">
        <DialogHeader><DialogTitle className="font-display">{t("mb_templates")}</DialogTitle></DialogHeader>
        {edit ? (
          <div className="space-y-3" data-testid="template-form">
            <div className="grid grid-cols-[1fr_120px] gap-3">
              <div className="space-y-1"><Label className="text-xs">{t("mb_tpl_name")}</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="tpl-name" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("language")}</Label>
                <Select value={edit.lang} onValueChange={(v) => setEdit({ ...edit, lang: v })}>
                  <SelectTrigger data-testid="tpl-lang"><SelectValue /></SelectTrigger>
                  <SelectContent>{["nl", "en", "es"].map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">{t("mb_subject")}</Label><Input value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} data-testid="tpl-subject" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("mb_message")}</Label><Textarea rows={9} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} data-testid="tpl-body" /></div>
            <p className="text-[11px] text-muted-foreground">{t("mb_tpl_placeholders")}: <span className="font-mono">{PLACEHOLDERS.join(" ")}</span></p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEdit(null)} data-testid="tpl-cancel">{t("cancel")}</Button>
              <Button onClick={save} disabled={busy || !edit.name || !edit.subject || !edit.body} data-testid="tpl-save">{t("save")}</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="max-h-[55vh] overflow-y-auto cb-scroll divide-y divide-border/60 rounded-xl border border-border">
              {rows.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`tpl-row-${r.id}`}>
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold">{r.lang.toUpperCase()}</span>
                  <span className="min-w-0 flex-1"><span className="block font-medium truncate">{r.name}</span><span className="block text-xs text-muted-foreground truncate">{r.subject}</span></span>
                  <Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`tpl-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(r)} data-testid={`tpl-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="tpl-close">{t("close")}</Button>
              <Button onClick={() => setEdit(blank())} data-testid="tpl-new"><Plus className="h-4 w-4 mr-1" />{t("mb_tpl_new")}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
